import { useState, useMemo } from "react";
import {
  Stack,
  Group,
  Select,
  Table,
  Pagination,
  Paper,
  Text,
  Skeleton,
  Center,
  Badge,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Button,
} from "@mantine/core";
import { IconRefresh, IconArrowLeft } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import {
  ReportDateRangePicker,
  getLastMonthRange,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import { useBillingStatusReport } from "../../hooks/useReports";
import { useCaseStatusesForFiltering } from "../../hooks/useAdmin";
import { usePhysicians } from "../../hooks/useReferenceData";
import { usePractices } from "../../hooks/admin/usePractices";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useBillingConfiguration } from "../../hooks/useBilling";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { isBillingAutomationAllowed } from "../../utils/manualTebraEntry";
import { listPrefixes } from "../../services/prefixService";
import {
  reportsService,
  type BillingStatusValue,
} from "../../services/reports.service";

const DEFAULT_RANGE: DateRange = getLastMonthRange();

const STATUS_COLORS: Record<BillingStatusValue, string> = {
  pending: "blue",
  submitting: "cyan",
  submitted: "yellow",
  billed: "green",
  rejected: "red",
  error: "orange",
  reconciliation_required: "grape",
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "submitting", label: "Submitting" },
  { value: "submitted", label: "Submitted" },
  { value: "billed", label: "Billed" },
  { value: "rejected", label: "Rejected" },
  { value: "error", label: "Error" },
  { value: "reconciliation_required", label: "Reconciliation required" },
];

export function BillingStatusReportContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [billingStatus, setBillingStatus] = useState<string>("");
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [prefixId, setPrefixId] = useState<string | null>(null);
  const [physicianId, setPhysicianId] = useState<string | null>(null);
  const [caseStatusId, setCaseStatusId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const canManageBilling = useHasPermission("billing.manage");
  const {
    data: billingConfiguration,
    isError: isBillingConfigurationError,
    isFetching: isFetchingBillingConfiguration,
    isLoading: isLoadingBillingConfiguration,
  } = useBillingConfiguration();
  const automationAllowed =
    !isBillingConfigurationError &&
    !isFetchingBillingConfiguration &&
    !isLoadingBillingConfiguration &&
    isBillingAutomationAllowed(billingConfiguration);

  const { data: practices = [] } = usePractices();
  const { data: physicians = [] } = usePhysicians();
  const { data: caseStatuses = [] } = useCaseStatusesForFiltering();

  const { data: prefixListResponse } = useQuery({
    queryKey: ["prefixes", "all"],
    queryFn: () => listPrefixes({ all: true }),
  });
  const prefixes = useMemo(
    () => prefixListResponse?.data ?? [],
    [prefixListResponse]
  );

  const practiceOptions = useMemo(
    () => practices.map((p) => ({ value: p.id, label: p.name })),
    [practices]
  );

  const prefixOptions = useMemo(
    () => prefixes.map((p) => ({ value: p.id, label: p.code })),
    [prefixes]
  );

  const caseStatusOptions = useMemo(() => {
    const baseOptions = caseStatuses.map((s) => ({
      value: s.id,
      label: s.name,
    }));
    // Add "All statuses" option and place it first or default
    return [{ value: "all", label: "All case statuses" }, ...baseOptions];
  }, [caseStatuses]);

  const params = useMemo(
    () => ({
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      billingStatus: (billingStatus || undefined) as
        | BillingStatusValue
        | undefined,
      practiceId: practiceId ?? undefined,
      prefixId: prefixId ?? undefined,
      physicianId: physicianId ?? undefined,
      caseStatusId: caseStatusId ?? undefined,
    }),
    [dateRange, billingStatus, practiceId, prefixId, physicianId, caseStatusId]
  );

  const { data, isLoading } = useBillingStatusReport(params, page);
  const totalPages = data
    ? Math.ceil(data.pagination.total / data.pagination.limit)
    : 1;

  const retryMutation = useMutation({
    mutationFn: (caseId: string) => reportsService.retryBilling(caseId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["reports", "billing-status"],
      });
      notifications.show({
        title: "Success",
        message: result.message || "Case resubmitted to Tebra successfully.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Could not retry billing.",
        color: "red",
      });
    },
  });

  const summary = data?.summary;

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Select
              label="Status"
              data={STATUS_OPTIONS}
              value={billingStatus}
              onChange={(v) => {
                setBillingStatus(v ?? "");
                setPage(1);
              }}
              w={150}
            />
            <Select
              label="Practice"
              placeholder="All practices"
              data={practiceOptions}
              value={practiceId}
              onChange={(v) => {
                setPracticeId(v);
                setPage(1);
              }}
              searchable
              clearable
              w={180}
            />
            <Select
              label="Case Prefix"
              placeholder="All prefixes"
              data={prefixOptions}
              value={prefixId}
              onChange={(v) => {
                setPrefixId(v);
                setPage(1);
              }}
              searchable
              clearable
              w={150}
            />
            <Select
              label="Referring Physician"
              placeholder="All physicians"
              data={physicians}
              value={physicianId}
              onChange={(v) => {
                setPhysicianId(v);
                setPage(1);
              }}
              searchable
              clearable
              w={180}
            />
            <Select
              label="Case Status"
              placeholder="All case statuses"
              data={caseStatusOptions}
              value={caseStatusId ?? ""}
              onChange={(v) => {
                // If they select empty or all, treat as empty (which defaults to 'Signed Off' in backend, or 'all' if selected)
                setCaseStatusId(v === "all" ? "all" : v);
                setPage(1);
              }}
              searchable
              clearable
              w={180}
            />
            <ReportDateRangePicker
              value={dateRange}
              onChange={(r) => {
                setDateRange(r);
                setPage(1);
              }}
            />
          </Group>
          <ReportExportButton
            filename={`billing-status-${labToday}.csv`}
            fetchData={() => reportsService.exportBillingStatus(params)}
          />
        </Group>
      </Paper>

      {summary && (
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
          {(Object.entries(summary) as [BillingStatusValue, number][]).map(
            ([status, count]) => (
              <Paper
                key={status}
                p="sm"
                withBorder
                style={{
                  borderLeft: `4px solid var(--mantine-color-${STATUS_COLORS[status]}-5)`,
                }}
              >
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  {status}
                </Text>
                <Text size="xl" fw={700}>
                  {count}
                </Text>
              </Paper>
            )
          )}
        </SimpleGrid>
      )}

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} h={36} />
            ))}
          </Stack>
        ) : data?.data.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">No cases for the selected period.</Text>
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Case #</Table.Th>
                  <Table.Th>Patient</Table.Th>
                  <Table.Th>Practice</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Billing Activity</Table.Th>
                  <Table.Th>Billed At</Table.Th>
                  <Table.Th>Tebra Encounter</Table.Th>
                  <Table.Th>Billing Error / Rejection</Table.Th>
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.data.map((row) => (
                  <Table.Tr key={row.caseId}>
                    <Table.Td>
                      <Text size="sm" c="blue">
                        {row.caseNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>{row.patientName}</Table.Td>
                    <Table.Td>{row.practiceName ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge
                        color={STATUS_COLORS[row.billingStatus] ?? "gray"}
                        variant="light"
                      >
                        {row.billingStatus}
                      </Badge>
                      {row.billingSubmissionMethod &&
                        (row.billingStatus === "submitted" ||
                          row.billingStatus === "billed") && (
                          <Badge
                            ml={4}
                            size="xs"
                            variant="dot"
                            color={
                              row.billingSubmissionMethod === "manual"
                                ? "blue"
                                : "grape"
                            }
                          >
                            {row.billingSubmissionMethod === "manual"
                              ? "Manual"
                              : "API"}
                          </Badge>
                        )}
                    </Table.Td>
                    <Table.Td>
                      {row.billingActivityAt
                        ? dayjs(row.billingActivityAt).format("MMM D, YYYY")
                        : "—"}
                    </Table.Td>
                    <Table.Td>
                      {row.billedAt
                        ? dayjs(row.billedAt).format("MMM D, YYYY")
                        : "—"}
                    </Table.Td>
                    <Table.Td>{row.tebraEncounterId ?? "—"}</Table.Td>
                    <Table.Td>
                      {row.billingStatus === "rejected" ||
                      row.billingStatus === "error" ? (
                        <Text size="xs" c="red">
                          {row.billingRejectionReason ?? "—"}
                        </Text>
                      ) : (
                        "—"
                      )}
                    </Table.Td>
                    <Table.Td>
                      {canManageBilling &&
                      automationAllowed &&
                      (row.billingStatus === "rejected" ||
                        row.billingStatus === "error") ? (
                        <Tooltip label="Retry billing">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="blue"
                            loading={retryMutation.isPending}
                            onClick={() => retryMutation.mutate(row.caseId)}
                          >
                            <IconRefresh size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}

export default function BillingStatusReportPage() {
  return (
    <Stack gap="lg" p="md">
      <Group>
        <Button
          component={Link}
          to="/reports"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
        >
          Reports
        </Button>
      </Group>
      <BillingStatusReportContent />
    </Stack>
  );
}
